'use client';

import { type EventSourceMessage, fetchEventSource } from '@microsoft/fetch-event-source';
import { useCallback, useEffect, useRef, useState } from 'react';

import { refreshAccessToken } from '@/shared/utils/auth';

interface UseSseStreamParams {
  path: string;
  enabled: boolean;
  escritorioId?: string;
  onMessage: (message: EventSourceMessage) => void;
  onOpen?: (context: { isReconnect: boolean }) => void;
}

const MAX_AUTH_FAILURES = 3;
const MAX_RECONNECT_DELAY_MS = 30000;
const RATE_LIMIT_BACKOFF_MS = 10000;
const CONTROL_EVENTS = new Set(['heartbeat', 'connected']);

export const useSseStream = ({
  path,
  enabled,
  escritorioId,
  onMessage,
  onOpen,
}: UseSseStreamParams): { isConnected: boolean } => {
  const [isConnected, setIsConnected] = useState(false);
  const [reconnectTrigger, setReconnectTrigger] = useState(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const authFailureCountRef = useRef(0);
  const handlersRef = useRef({ onMessage, onOpen });

  useEffect(() => {
    handlersRef.current = { onMessage, onOpen };
  }, [onMessage, onOpen]);

  const scheduleReconnect = useCallback((abortSignal: AbortSignal) => {
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    const delay = Math.min(1000 * 2 ** reconnectAttemptsRef.current, MAX_RECONNECT_DELAY_MS);
    reconnectAttemptsRef.current += 1;
    reconnectTimerRef.current = setTimeout(() => {
      if (!abortSignal.aborted) setReconnectTrigger((v) => v + 1);
    }, delay);
  }, []);

  const connect = useCallback(async () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const streamUrl = new URL(`${process.env.NEXT_PUBLIC_API_URL}${path}`, window.location.origin);
    const headers: Record<string, string> = { Accept: 'text/event-stream' };
    if (escritorioId) headers['X-Workspace-Id'] = escritorioId;

    try {
      await fetchEventSource(streamUrl.toString(), {
        method: 'GET',
        headers,
        credentials: 'include',
        signal: abortController.signal,
        onopen: async (response) => {
          if (response.ok && response.headers.get('content-type')?.includes('text/event-stream')) {
            const isReconnect = reconnectAttemptsRef.current > 0;
            setIsConnected(true);
            authFailureCountRef.current = 0;
            reconnectAttemptsRef.current = 0;
            handlersRef.current.onOpen?.({ isReconnect });
            return;
          }
          if (response.status === 401) throw new Error('Unauthorized');
          if (response.status === 429) {
            await new Promise((resolve) => setTimeout(resolve, RATE_LIMIT_BACKOFF_MS));
            throw new Error('Rate limited');
          }
          if (response.status >= 400 && response.status < 500) {
            throw new Error(`Fatal error: ${response.status}`);
          }
        },
        onmessage: (message) => {
          if (message.event && CONTROL_EVENTS.has(message.event)) return;
          handlersRef.current.onMessage(message);
        },
        onclose: () => {
          setIsConnected(false);
          if (!abortController.signal.aborted) scheduleReconnect(abortController.signal);
        },
        onerror: (err) => {
          setIsConnected(false);
          if (err instanceof Error && err.message === 'Unauthorized') {
            authFailureCountRef.current += 1;
            if (authFailureCountRef.current >= MAX_AUTH_FAILURES) throw err;
            void refreshAccessToken().then((success) => {
              if (success) {
                authFailureCountRef.current = 0;
                scheduleReconnect(abortController.signal);
              }
            });
            throw err;
          }
          scheduleReconnect(abortController.signal);
          throw err;
        },
      });
    } catch {
      setIsConnected(false);
    }
  }, [path, escritorioId, scheduleReconnect]);

  useEffect(() => {
    if (enabled) void connect();
    return () => {
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [enabled, connect, reconnectTrigger]);

  return { isConnected };
};
